// Trash bin — lists soft-deleted employees so a manager can restore or
// permanently delete them (P0: safety net past the 5-second undo-toast window).
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, RotateCcw, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import PaperPlaneEmpty from "@/components/PaperPlaneEmpty";

export default function TrashModal({ open, onClose, onChange }) {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/deleted");
      setList(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open]);

  const restore = async (u) => {
    try {
      await api.post(`/users/${u._id}/restore`);
      toast.success(t("trash_restored"));
      load();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  const doPurge = async () => {
    const u = confirmPurge;
    setConfirmPurge(null);
    if (!u) return;
    try {
      await api.delete(`/users/${u._id}/permanent`);
      toast.success(t("trash_purged"));
      load();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("nl", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={onClose}
          data-testid="trash-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="card w-full max-w-xl p-0 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            <div className="flex items-center justify-between p-6 border-b hairline">
              <div>
                <div className="eyebrow">{t("trash_eyebrow")}</div>
                <h3 className="font-display text-[22px] font-bold tracking-tight mt-0.5">
                  {t("trash_title")}
                </h3>
                <p className="text-[12.5px] text-[hsl(var(--text-muted))] mt-1 max-w-md">
                  {t("trash_subtitle")}
                </p>
              </div>
              <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] transition-colors" data-testid="trash-close">
                <X size={16} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {loading ? (
                <div className="py-12 text-center text-[13px] text-[hsl(var(--text-muted))]">{t("loading")}…</div>
              ) : list.length === 0 ? (
                <div className="py-10">
                  <PaperPlaneEmpty size={110} />
                  <div className="text-center mt-3">
                    <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))]">
                      {t("trash_empty_title")}
                    </div>
                    <div className="text-[12.5px] text-[hsl(var(--text-muted))] mt-1 max-w-[240px] mx-auto">
                      {t("trash_empty_hint")}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="divide-y hairline">
                  {list.map((u) => (
                    <div key={u._id} className="flex items-center gap-3 px-5 py-3.5" data-testid={`trash-row-${u._id}`}>
                      <div className="w-10 h-10 rounded-full grid place-items-center bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] shrink-0">
                        <UsersIcon size={16} strokeWidth={1.9} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-semibold text-[hsl(var(--text-strong))] truncate">{u.name}</div>
                        <div className="text-[11.5px] text-[hsl(var(--text-muted))] truncate">
                          {u.email} · {t("trash_deleted_at")} {fmtDate(u.deleted_at)}
                        </div>
                      </div>
                      <button
                        onClick={() => restore(u)}
                        data-testid={`trash-restore-${u._id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[12px] font-semibold border hairline hover:bg-[hsl(var(--primary-soft))] hover:text-[hsl(var(--primary))] transition-colors"
                      >
                        <RotateCcw size={12} strokeWidth={2.25} />
                        {t("trash_restore")}
                      </button>
                      <button
                        onClick={() => setConfirmPurge(u)}
                        data-testid={`trash-purge-${u._id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[12px] font-semibold text-[hsl(var(--danger))] border border-[hsl(var(--danger)_/_0.25)] hover:bg-[hsl(var(--danger-soft))] transition-colors"
                      >
                        <Trash2 size={12} strokeWidth={2.25} />
                        {t("trash_purge")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          <ConfirmDialog
            open={!!confirmPurge}
            title={t("trash_confirm_title")}
            body={confirmPurge ? t("trash_confirm_body").replace("{name}", confirmPurge.name || confirmPurge.email) : ""}
            confirmLabel={t("trash_purge")}
            onConfirm={doPurge}
            onCancel={() => setConfirmPurge(null)}
            testid="confirm-trash"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
